import { useTranslation } from 'react-i18next';

import type { PlanSchemeSlot } from '../../../../api/plan-options';
import { Icon } from '../../../../components/Icon';
import type { PlanScheme } from '../../../../types/admin';
import type { PlanDraft } from './draft';
import {
  customAllocatedCount,
  effectiveDurationDays,
  manufacturedThisStage,
  nextDraftId,
  PLAN_LIMITS,
  retentionCapCount,
  suggestedDurationDays,
  type DraftAlignerItem,
} from './draft';
import styles from './PlanOptions.module.scss';

type Props = {
  readonly draft: PlanDraft;
  readonly isMain: boolean;
  readonly currency: string;
  readonly onChange: (patch: Partial<PlanDraft>) => void;
  readonly renderSchemeControl: (target: PlanSchemeTarget) => React.ReactNode;
};

export type PlanSchemeTarget =
  | {
      readonly kind: 'slot';
      readonly slot: PlanSchemeSlot;
      readonly label: string;
      readonly scheme: PlanScheme | null;
      readonly schemes: readonly PlanScheme[];
    }
  | {
      readonly kind: 'clinical';
      readonly elementId: string;
      readonly label: string;
      readonly scheme: PlanScheme | null;
      readonly schemes: readonly PlanScheme[];
    };

type FieldProps = {
  readonly label: string;
  readonly children: React.ReactNode;
  readonly full?: boolean;
};

function Field({ label, children, full = false }: FieldProps) {
  return (
    <label className={`${styles.field} ${full ? styles.fieldFull : ''}`}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function EditorSection({
  title,
  hint,
  children,
  action,
}: {
  readonly title: string;
  readonly hint?: string;
  readonly children: React.ReactNode;
  readonly action?: React.ReactNode;
}) {
  return (
    <section className={styles.editorSection}>
      <header className={styles.editorSectionHeader}>
        <div>
          <h6>{title}</h6>
          {hint && <p>{hint}</p>}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className={styles.rowRemove} aria-label={label} onClick={onClick}>
      <Icon name="delete" size={14} />
    </button>
  );
}

export function PlanFieldsEditor({
  draft,
  isMain,
  currency,
  onChange,
  renderSchemeControl,
}: Props) {
  const { t } = useTranslation();
  const manufactured = manufacturedThisStage(draft);
  const allocated = customAllocatedCount(draft);
  const retention = retentionCapCount(draft);
  const allocationMatches = manufactured === allocated;
  const suffix = currency.trim().toUpperCase() === 'UAH' ? t('planOptions.currencyUah') : currency;
  const itemTypeLabel = (kind: DraftAlignerItem['kind']): string =>
    t(`planOptions.alignerKind_${kind}`);
  const defaultAlignerItems = (): readonly DraftAlignerItem[] => {
    const rows: DraftAlignerItem[] = [];
    const add = (
      kind: DraftAlignerItem['kind'],
      jaw: DraftAlignerItem['jaw'],
      quantity: string,
    ) => rows.push({
      id: nextDraftId('aligner'),
      type: itemTypeLabel(kind),
      kind,
      jaw,
      quantity,
      unitPrice: draft.standardUnitPrice,
    });
    if (Number(draft.upperCount) > 0) add('REGULAR', 'upper', draft.upperCount);
    if (Number(draft.lowerCount) > 0) add('REGULAR', 'lower', draft.lowerCount);
    if (Number(draft.setupAlignersCount) > 0) add('SETUP', 'none', draft.setupAlignersCount);
    if (rows.length === 0) add('REGULAR', 'upper', '0');
    return rows;
  };

  return (
    <>
      <EditorSection title={t('planOptions.sectionIdentity')}>
        <div className={styles.fieldGrid}>
          <Field label={t('planOptions.planTitle')} full>
            <input
              type="text"
              value={draft.title}
              maxLength={255}
              onChange={(event) => onChange({ title: event.target.value })}
            />
          </Field>
        </div>
      </EditorSection>

      <EditorSection
        title={t('planOptions.clinicalComment')}
        hint={t('planOptions.clinicalCommentHint')}
      >
        <Field label={t('planOptions.clinicalComment')}>
          <textarea
            rows={5}
            required
            maxLength={4000}
            value={draft.clinicalComment}
            placeholder={t('planOptions.clinicalCommentPlaceholder')}
            onChange={(event) => onChange({ clinicalComment: event.target.value })}
          />
        </Field>
      </EditorSection>

      <EditorSection title={t('planOptions.wholeCourse')} hint={t('planOptions.wholeCourseHint')}>
        <div className={styles.fieldGrid}>
          <Field label={t('planOptions.estimatedStages')}>
            <input
              type="number"
              min={1}
              max={PLAN_LIMITS.estimatedStages}
              required
              value={draft.estimatedStages}
              onChange={(event) => onChange({ estimatedStages: event.target.value })}
            />
          </Field>
          <Field label={t('planOptions.maxCourseAligners')}>
            <input
              type="number"
              min={0}
              max={PLAN_LIMITS.maxCourseAligners}
              required
              value={draft.maxCourseAligners}
              onChange={(event) => onChange({ maxCourseAligners: event.target.value })}
            />
          </Field>
        </div>
      </EditorSection>

      <EditorSection title={t('planOptions.currentStage')} hint={t('planOptions.currentStageHint')}>
        <div className={styles.fieldGridThree}>
          <Field label={t('planOptions.upperJaw')}>
            <input type="number" min={0} max={PLAN_LIMITS.jawAligners} required value={draft.upperCount} onChange={(event) => onChange({ upperCount: event.target.value })} />
          </Field>
          <Field label={t('planOptions.lowerJaw')}>
            <input type="number" min={0} max={PLAN_LIMITS.jawAligners} required value={draft.lowerCount} onChange={(event) => onChange({ lowerCount: event.target.value })} />
          </Field>
          <Field label={t('planOptions.setupAligners')}>
            <input type="number" min={0} max={PLAN_LIMITS.jawAligners} required value={draft.setupAlignersCount} onChange={(event) => onChange({ setupAlignersCount: event.target.value })} />
          </Field>
        </div>
        <div className={styles.fieldGrid}>
          <Field label={t('planOptions.daysPerAligner')}>
            <input type="number" min={1} max={PLAN_LIMITS.daysPerAligner} required value={draft.daysPerAligner} onChange={(event) => onChange({ daysPerAligner: event.target.value })} />
          </Field>
          <Field label={t('planOptions.durationOverride', { days: suggestedDurationDays(draft) })}>
            <input type="number" min={0} max={PLAN_LIMITS.durationDays} value={draft.durationOverrideDays} placeholder={suggestedDurationDays(draft).toString()} onChange={(event) => onChange({ durationOverrideDays: event.target.value })} />
          </Field>
        </div>
        <p className={styles.derivedLine}>
          {t('planOptions.stageDerived', {
            manufactured,
            duration: effectiveDurationDays(draft),
          })}
        </p>
      </EditorSection>

      <EditorSection
        title={t('planOptions.alignerPricing')}
        hint={t('planOptions.alignerPricingHint')}
        action={
          <div className={styles.segmented} aria-label={t('planOptions.pricingMode')}>
            {(['AUTO', 'CUSTOM'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                aria-pressed={draft.alignerPricingMode === mode}
                onClick={() =>
                  onChange({
                    alignerPricingMode: mode,
                    ...(mode === 'CUSTOM' && draft.alignerItems.length === 0
                      ? {
                          alignerItems: defaultAlignerItems(),
                        }
                      : {}),
                  })
                }
              >
                {mode === 'AUTO' ? t('planOptions.pricingAuto') : t('planOptions.pricingCustom')}
              </button>
            ))}
          </div>
        }
      >
        {draft.alignerPricingMode === 'AUTO' ? (
          <div className={styles.autoPricing}>
            <span>{t('planOptions.autoPricingLine', { manufactured })}</span>
            <Field label={t('planOptions.unitPrice', { currency: suffix })}>
              <input type="number" min={0} step="0.01" required value={draft.standardUnitPrice} onChange={(event) => onChange({ standardUnitPrice: event.target.value })} />
            </Field>
          </div>
        ) : (
          <div className={styles.repeatableList}>
            <p className={allocationMatches ? styles.allocationOk : styles.allocationError} role={allocationMatches ? undefined : 'alert'}>
              {t(allocationMatches ? 'planOptions.allocationOk' : 'planOptions.allocationMismatch', { allocated, manufactured, retention })}
            </p>
            {draft.alignerItems.map((item) => (
              <div key={item.id} className={styles.alignerRow}>
                <Field label={t('planOptions.alignerJaw')}>
                  <select
                    value={item.jaw}
                    disabled={item.kind === 'SETUP'}
                    onChange={(event) => onChange({ alignerItems: draft.alignerItems.map((entry) => entry.id === item.id ? { ...entry, jaw: event.target.value as DraftAlignerItem['jaw'] } : entry) })}
                  >
                    {item.kind === 'SETUP' || item.jaw === 'none' ? <option value="none">{t('planOptions.alignerJawNone')}</option> : null}
                    <option value="upper">{t('planOptions.alignerJawUpper')}</option>
                    <option value="lower">{t('planOptions.alignerJawLower')}</option>
                  </select>
                </Field>
                <Field label={t('planOptions.alignerKind')}>
                  <select
                    value={item.kind}
                    onChange={(event) => {
                      const kind = event.target.value as DraftAlignerItem['kind'];
                      onChange({
                        alignerItems: draft.alignerItems.map((entry) => entry.id === item.id
                          ? {
                              ...entry,
                              kind,
                              type: kind === 'CUSTOM' ? '' : itemTypeLabel(kind),
                              jaw: kind === 'SETUP' ? 'none' : entry.jaw === 'none' ? 'upper' : entry.jaw,
                            }
                          : entry),
                      });
                    }}
                  >
                    <option value="REGULAR">{t('planOptions.alignerKind_REGULAR')}</option>
                    <option value="WITH_OVERLAYS">{t('planOptions.alignerKind_WITH_OVERLAYS')}</option>
                    <option value="SETUP">{t('planOptions.alignerKind_SETUP')}</option>
                    <option value="RETENTION_CAP">{t('planOptions.alignerKind_RETENTION_CAP')}</option>
                    <option value="CUSTOM">{t('planOptions.alignerKind_CUSTOM')}</option>
                  </select>
                  {item.kind === 'CUSTOM' && (
                    <input aria-label={t('planOptions.customAlignerName')} required value={item.type} placeholder={t('planOptions.customAlignerName')} onChange={(event) => onChange({ alignerItems: draft.alignerItems.map((entry) => entry.id === item.id ? { ...entry, type: event.target.value } : entry) })} />
                  )}
                </Field>
                <Field label={t('planOptions.quantity')}>
                  <input type="number" min={0} required value={item.quantity} onChange={(event) => onChange({ alignerItems: draft.alignerItems.map((entry) => entry.id === item.id ? { ...entry, quantity: event.target.value } : entry) })} />
                </Field>
                <Field label={t('planOptions.unitPrice', { currency: suffix })}>
                  <input type="number" min={0} step="0.01" required value={item.unitPrice} onChange={(event) => onChange({ alignerItems: draft.alignerItems.map((entry) => entry.id === item.id ? { ...entry, unitPrice: event.target.value } : entry) })} />
                </Field>
                <RemoveButton label={t('planOptions.removeAlignerItem')} onClick={() => onChange({ alignerItems: draft.alignerItems.filter((entry) => entry.id !== item.id) })} />
              </div>
            ))}
            <button type="button" disabled={draft.alignerItems.length >= PLAN_LIMITS.presentationItems} className={styles.itemAdd} onClick={() => onChange({ alignerItems: [...draft.alignerItems, { id: nextDraftId('aligner'), type: itemTypeLabel('RETENTION_CAP'), kind: 'RETENTION_CAP', jaw: 'upper', quantity: '1', unitPrice: draft.standardUnitPrice }] })}>
              {t('planOptions.addAlignerItem')}
            </button>
          </div>
        )}
      </EditorSection>

      <EditorSection title={t('planOptions.structuredElements')} hint={t('planOptions.structuredElementsHint')}>
        <div className={styles.clinicalBlocks}>
          <ClinicalToggle label={t('planOptions.attachments')} checked={draft.attachments.enabled} onChange={(enabled) => onChange({ attachments: { ...draft.attachments, enabled } })}>
            <div className={styles.fieldGrid}>
              <Field label={t('planOptions.itemType')}>
                <select value={draft.attachments.type} onChange={(event) => onChange({ attachments: { ...draft.attachments, type: event.target.value as PlanDraft['attachments']['type'] } })}>
                  <option value="Composite">Composite</option><option value="Negative">Negative</option><option value="Other">Other</option>
                </select>
              </Field>
              {draft.attachments.type === 'Composite' && (
                <Field label={t('planOptions.optionalQuantity')}><input type="number" min={0} value={draft.attachments.quantity} onChange={(event) => onChange({ attachments: { ...draft.attachments, quantity: event.target.value } })} /></Field>
              )}
              <Field label={t('planOptions.doctorComment')} full><input value={draft.attachments.comment} onChange={(event) => onChange({ attachments: { ...draft.attachments, comment: event.target.value } })} /></Field>
            </div>
            {renderSchemeControl({ kind: 'slot', slot: 'attachments', label: t('planOptions.attachments'), scheme: draft.attachments.scheme, schemes: draft.attachments.schemes })}
          </ClinicalToggle>

          <ClinicalToggle label={t('planOptions.buttons')} checked={draft.buttons.enabled} onChange={(enabled) => onChange({ buttons: { ...draft.buttons, enabled } })}>
            <PaidBlockFields quantityLabel={t('planOptions.quantity')} quantity={draft.buttons.quantity} type={draft.buttons.type} unitPrice={draft.buttons.unitPrice} currency={suffix} onQuantity={(quantity) => onChange({ buttons: { ...draft.buttons, quantity } })} onType={(type) => onChange({ buttons: { ...draft.buttons, type } })} onUnitPrice={(unitPrice) => onChange({ buttons: { ...draft.buttons, unitPrice } })} />
            {renderSchemeControl({ kind: 'slot', slot: 'buttons', label: t('planOptions.buttons'), scheme: draft.buttons.scheme, schemes: draft.buttons.schemes })}
          </ClinicalToggle>

          <ClinicalToggle label={t('planOptions.elastics')} checked={draft.elastics.enabled} onChange={(enabled) => onChange({ elastics: { ...draft.elastics, enabled } })}>
            <PaidBlockFields quantityLabel={t('planOptions.sets')} quantity={draft.elastics.sets} type={draft.elastics.type} unitPrice={draft.elastics.unitPrice} currency={suffix} onQuantity={(sets) => onChange({ elastics: { ...draft.elastics, sets } })} onType={(type) => onChange({ elastics: { ...draft.elastics, type } })} onUnitPrice={(unitPrice) => onChange({ elastics: { ...draft.elastics, unitPrice } })} />
            {renderSchemeControl({ kind: 'slot', slot: 'elastics', label: t('planOptions.elastics'), scheme: draft.elastics.scheme, schemes: draft.elastics.schemes })}
          </ClinicalToggle>

          <ClinicalToggle label={t('planOptions.separation')} checked={draft.separation.enabled} onChange={(enabled) => onChange({ separation: { ...draft.separation, enabled } })}>
            <div className={styles.fieldGrid}>
              <Field label={t('planOptions.doctorComment')}><input value={draft.separation.comment} onChange={(event) => onChange({ separation: { ...draft.separation, comment: event.target.value } })} /></Field>
              <Field label={t('planOptions.price', { currency: suffix })}><input type="number" min={0} step="0.01" value={draft.separation.price} onChange={(event) => onChange({ separation: { ...draft.separation, price: event.target.value } })} /></Field>
            </div>
            {renderSchemeControl({ kind: 'slot', slot: 'separation', label: t('planOptions.separation'), scheme: draft.separation.scheme, schemes: draft.separation.schemes })}
          </ClinicalToggle>

          <div className={styles.repeatableHeader}>
            <span>{t('planOptions.otherClinicalElements')}</span>
            <button type="button" disabled={draft.clinicalElements.length >= PLAN_LIMITS.presentationItems} className={styles.itemAdd} onClick={() => onChange({ clinicalElements: [...draft.clinicalElements, { id: nextDraftId('clinical'), title: '', description: '', paid: false, price: '0', scheme: null, schemes: [] }] })}>{t('planOptions.addClinicalElement')}</button>
          </div>
          {draft.clinicalElements.map((element) => (
            <div key={element.id} className={styles.clinicalElementRow}>
              <div className={styles.fieldGrid}>
                <Field label={t('planOptions.elementTitle')}><input required value={element.title} onChange={(event) => onChange({ clinicalElements: draft.clinicalElements.map((entry) => entry.id === element.id ? { ...entry, title: event.target.value } : entry) })} /></Field>
                <label className={styles.inlineToggle}><input type="checkbox" checked={element.paid} onChange={(event) => onChange({ clinicalElements: draft.clinicalElements.map((entry) => entry.id === element.id ? { ...entry, paid: event.target.checked } : entry) })} />{t('planOptions.paid')}</label>
                <Field label={t('planOptions.elementDescription')} full><input value={element.description} onChange={(event) => onChange({ clinicalElements: draft.clinicalElements.map((entry) => entry.id === element.id ? { ...entry, description: event.target.value } : entry) })} /></Field>
                {element.paid && <Field label={t('planOptions.price', { currency: suffix })}><input type="number" min={0} step="0.01" value={element.price} onChange={(event) => onChange({ clinicalElements: draft.clinicalElements.map((entry) => entry.id === element.id ? { ...entry, price: event.target.value } : entry) })} /></Field>}
              </div>
              {renderSchemeControl({ kind: 'clinical', elementId: element.id, label: element.title.trim() || t('planOptions.otherClinicalElements'), scheme: element.scheme, schemes: element.schemes })}
              <RemoveButton label={t('planOptions.removeClinicalElement')} onClick={() => onChange({ clinicalElements: draft.clinicalElements.filter((entry) => entry.id !== element.id) })} />
            </div>
          ))}
        </div>
      </EditorSection>

      <EditorSection title={t('planOptions.depositDiscounts')}>
        <Field label={t('planOptions.depositAmount', { currency: suffix })}>
          <input type="number" min={0} step="0.01" value={draft.depositAmount} onChange={(event) => onChange({ depositAmount: event.target.value })} />
        </Field>
        <div className={styles.repeatableHeader}>
          <span>{t('planOptions.discountsPromotions')}</span>
          <button type="button" disabled={draft.discounts.length >= PLAN_LIMITS.presentationItems} className={styles.itemAdd} onClick={() => onChange({ discounts: [...draft.discounts, { id: nextDraftId('discount'), name: '', comment: '', amount: '0' }] })}>{t('planOptions.addDiscount')}</button>
        </div>
        {draft.discounts.map((discount) => (
          <div key={discount.id} className={styles.discountRow}>
            <Field label={t('planOptions.discountName')}><input required value={discount.name} onChange={(event) => onChange({ discounts: draft.discounts.map((entry) => entry.id === discount.id ? { ...entry, name: event.target.value } : entry) })} /></Field>
            <Field label={t('planOptions.discountComment')}><input value={discount.comment} onChange={(event) => onChange({ discounts: draft.discounts.map((entry) => entry.id === discount.id ? { ...entry, comment: event.target.value } : entry) })} /></Field>
            <Field label={t('planOptions.discountAmount', { currency: suffix })}><input type="number" min={0} step="0.01" required value={discount.amount} onChange={(event) => onChange({ discounts: draft.discounts.map((entry) => entry.id === discount.id ? { ...entry, amount: event.target.value } : entry) })} /></Field>
            <RemoveButton label={t('planOptions.removeDiscount')} onClick={() => onChange({ discounts: draft.discounts.filter((entry) => entry.id !== discount.id) })} />
          </div>
        ))}
      </EditorSection>

      {!isMain && (
        <EditorSection title={t('planOptions.keyDifference')}>
          <Field label={t('planOptions.keyDifference')}>
            <textarea rows={3} value={draft.keyDifference} placeholder={t('planOptions.keyDifferencePlaceholder')} onChange={(event) => onChange({ keyDifference: event.target.value })} />
          </Field>
        </EditorSection>
      )}

    </>
  );
}

function ClinicalToggle({ label, checked, onChange, children }: { label: string; checked: boolean; onChange: (checked: boolean) => void; children: React.ReactNode }) {
  return <div className={styles.clinicalBlock}><label className={styles.inlineToggle}><input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />{label}</label>{checked && <div className={styles.clinicalBlockBody}>{children}</div>}</div>;
}

function PaidBlockFields({ quantityLabel, quantity, type, unitPrice, currency, onQuantity, onType, onUnitPrice }: { quantityLabel: string; quantity: string; type: string; unitPrice: string; currency: string; onQuantity: (value: string) => void; onType: (value: string) => void; onUnitPrice: (value: string) => void }) {
  const { t } = useTranslation();
  return <div className={styles.fieldGridThree}><Field label={quantityLabel}><input type="number" min={0} value={quantity} onChange={(event) => onQuantity(event.target.value)} /></Field><Field label={t('planOptions.typeComment')}><input value={type} onChange={(event) => onType(event.target.value)} /></Field><Field label={t('planOptions.unitPrice', { currency })}><input type="number" min={0} step="0.01" value={unitPrice} onChange={(event) => onUnitPrice(event.target.value)} /></Field></div>;
}
